import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileDownloadUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtMoney, fmtDate, fmtDateTime } from "@/lib/helpers";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ArrowLeft, FileText, Sparkles, Clock, Star, CheckCircle2, Send,
  MessageSquare, Cpu, Download, Loader2,
} from "lucide-react";

export default function RFQDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [msg, setMsg] = useState("");
  const [quoteForm, setQuoteForm] = useState({ unit_price: "", lead_time_days: "", notes: "" });
  const [sendForm, setSendForm] = useState({ source_quote_id: "", unit_price: "", lead_time_days: "", note: "" });
  const [busy, setBusy] = useState(false);
  const msgEnd = useRef(null);

  const loadAll = () => {
    api.get(`/rfqs/${id}`).then((r) => setRfq(r.data)).catch(() => toast.error("RFQ not found"));
    api.get(`/rfqs/${id}/quotes`).then((r) => setQuotes(r.data)).catch(() => {});
    api.get(`/rfqs/${id}/messages`).then((r) => setMsgs(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { msgEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  if (!rfq) return <div className="p-8 font-mono text-sm text-muted-foreground">Loading RFQ…</div>;

  const isBuyer = user.role === "buyer";
  const isSupplier = user.role === "supplier";
  const isAdmin = user.role === "admin";
  const myQuote = quotes.find((q) => q.supplier_id === user.id);
  const cq = rfq.customer_quote;

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    const text = msg;
    setMsg("");
    try {
      const { data } = await api.post(`/rfqs/${id}/messages`, { text });
      setMsgs((m) => [...m, data]);
    } catch { toast.error("Failed to send"); }
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/rfqs/${id}/quotes`, {
        unit_price: Number(quoteForm.unit_price),
        lead_time_days: Number(quoteForm.lead_time_days),
        notes: quoteForm.notes,
      });
      toast.success(myQuote ? "Quote updated" : "Quote submitted for review");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const acceptCustomerQuote = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/rfqs/${id}/accept-quote`);
      toast.success("Quote accepted — order created");
      nav(`/orders/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const selectSourceQuote = (q) => setSendForm({
    source_quote_id: q.id, unit_price: String(q.unit_price), lead_time_days: String(q.lead_time_days), note: "",
  });

  const sendToCustomer = async (e) => {
    e.preventDefault();
    if (!sendForm.source_quote_id) { toast.error("Select a supplier quote first"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/rfqs/${id}/send-quote`, {
        source_quote_id: sendForm.source_quote_id,
        unit_price: Number(sendForm.unit_price),
        lead_time_days: Number(sendForm.lead_time_days),
        note: sendForm.note,
      });
      toast.success("Quote sent to customer");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const specs = [
    ["Process", rfq.process], ["Material", rfq.material], ["Quantity", rfq.quantity],
    ["Tolerance", rfq.tolerance || "—"], ["Finish", rfq.finish || "—"], ["Deadline", rfq.deadline ? fmtDate(rfq.deadline) : "—"],
  ];

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-tech text-muted-foreground mb-1">RFQ · {rfq.id.slice(-6)}</div>
          <h1 className="text-3xl font-bold tracking-tight">{rfq.title}</h1>
          <div className="mt-2 text-sm text-muted-foreground">{rfq.buyer_company || rfq.buyer_name} · {fmtDate(rfq.created_at)}</div>
        </div>
        <StatusBadge status={rfq.status} className="mt-2" />
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Specs */}
          <div className="border border-border rounded-sm bg-card">
            <div className="px-5 py-3 border-b border-border label-tech text-muted-foreground">Specifications</div>
            <div className="grid grid-cols-2 sm:grid-cols-3">
              {specs.map(([k, v]) => (
                <div key={k} className="p-5 border-r border-b border-border">
                  <div className="label-tech text-muted-foreground">{k}</div>
                  <div className="mt-1.5 font-medium">{v}</div>
                </div>
              ))}
            </div>
            {rfq.notes && (
              <div className="p-5 border-t border-border">
                <div className="label-tech text-muted-foreground mb-1.5">Notes</div>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{rfq.notes}</p>
              </div>
            )}
          </div>

          {/* AI summary */}
          {rfq.ai_summary && (
            <div className="border border-brand/30 bg-brand-soft/50 rounded-sm p-5" data-testid="ai-summary">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-brand" />
                <span className="label-tech text-brand">AI manufacturing analysis</span>
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{rfq.ai_summary}</p>
            </div>
          )}

          {/* Files */}
          <div className="border border-border rounded-sm bg-card">
            <div className="px-5 py-3 border-b border-border label-tech text-muted-foreground">Attached files ({rfq.files?.length || 0})</div>
            <div className="p-5 space-y-2">
              {(!rfq.files || rfq.files.length === 0) && <div className="text-sm text-muted-foreground">No files attached.</div>}
              {rfq.files?.map((f) => (
                <a key={f.file_id} href={fileDownloadUrl(f.file_id)} target="_blank" rel="noreferrer" data-testid={`file-link-${f.file_id}`}
                  className="flex items-center gap-3 border border-border px-3 py-2 rounded-sm hover:border-brand hover:bg-secondary/50 transition-colors">
                  <FileText className="h-4 w-4 text-brand" />
                  <span className="text-sm flex-1 truncate">{f.original_filename}</span>
                  <span className="text-xs font-mono text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
                  <Download className="h-4 w-4 text-muted-foreground" />
                </a>
              ))}
            </div>
          </div>

          {/* Customer quote (buyer) */}
          {isBuyer && (
            <div className="border border-border rounded-sm bg-card" data-testid="customer-quote-panel">
              <div className="px-5 py-3 border-b border-border label-tech text-muted-foreground">Your quote</div>
              {!cq ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  Your request is under review. The Nepton team is sourcing the best supplier and will share a quote here shortly.
                </div>
              ) : (
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="label-tech text-muted-foreground">Quoted by</div>
                      <div className="mt-1 font-heading text-lg font-semibold">Nepton</div>
                    </div>
                    <StatusBadge status={cq.status === "accepted" ? "accepted" : "sent_to_customer"} />
                  </div>
                  <div className="mt-5 grid grid-cols-3 border-t border-l border-border rounded-sm overflow-hidden">
                    <div className="p-4 border-r border-b border-border">
                      <div className="label-tech text-muted-foreground">Unit price</div>
                      <div className="mt-1 font-mono font-medium">{fmtMoney(cq.unit_price)}</div>
                    </div>
                    <div className="p-4 border-r border-b border-border">
                      <div className="label-tech text-muted-foreground">Total</div>
                      <div className="mt-1 font-mono font-semibold text-lg">{fmtMoney(cq.total_price)}</div>
                    </div>
                    <div className="p-4 border-r border-b border-border">
                      <div className="label-tech text-muted-foreground">Lead time</div>
                      <div className="mt-1 font-mono font-medium">{cq.lead_time_days} days</div>
                    </div>
                  </div>
                  {cq.note && <p className="mt-4 text-sm text-muted-foreground">{cq.note}</p>}
                  <div className="mt-5">
                    {cq.status === "accepted" || rfq.status === "ordered" ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-700 text-sm font-medium"><CheckCircle2 className="h-4 w-4" /> Accepted — order placed</span>
                    ) : (
                      <button data-testid="accept-customer-quote" disabled={busy} onClick={acceptCustomerQuote}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-brand text-white rounded-sm hover:bg-brand-dark disabled:opacity-60 transition-colors">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Accept & place order
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Admin quote review + send to customer */}
          {isAdmin && (
            <div className="border border-border rounded-sm bg-card" data-testid="admin-quote-panel">
              <div className="px-5 py-3 border-b border-border label-tech text-muted-foreground">Supplier quotes ({quotes.length}) — internal</div>
              {quotes.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">No supplier quotes submitted yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="label-tech text-muted-foreground border-b border-border">
                        <th className="text-left px-5 py-3 font-normal">Supplier</th>
                        <th className="text-right px-3 py-3 font-normal">Unit</th>
                        <th className="text-right px-3 py-3 font-normal">Total</th>
                        <th className="text-center px-3 py-3 font-normal">Lead</th>
                        <th className="text-center px-3 py-3 font-normal">Rating</th>
                        <th className="text-center px-3 py-3 font-normal">Status</th>
                        <th className="text-right px-5 py-3 font-normal">Select</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quotes.map((q) => (
                        <tr key={q.id} data-testid={`supplier-quote-row-${q.id}`} className={cn("border-b border-border last:border-0", sendForm.source_quote_id === q.id && "bg-brand-soft/60")}>
                          <td className="px-5 py-4">
                            <div className="font-medium">{q.supplier_name}</div>
                            {q.notes && <div className="text-xs text-muted-foreground mt-0.5 max-w-xs truncate">{q.notes}</div>}
                          </td>
                          <td className="px-3 py-4 text-right font-mono">{fmtMoney(q.unit_price)}</td>
                          <td className="px-3 py-4 text-right font-mono font-semibold">{fmtMoney(q.total_price)}</td>
                          <td className="px-3 py-4 text-center font-mono">{q.lead_time_days}d</td>
                          <td className="px-3 py-4 text-center"><span className="inline-flex items-center gap-1 font-mono"><Star className="h-3 w-3 fill-brand text-brand" />{q.supplier_rating}</span></td>
                          <td className="px-3 py-4 text-center"><StatusBadge status={q.status} /></td>
                          <td className="px-5 py-4 text-right">
                            <button data-testid={`select-quote-${q.id}`} disabled={rfq.status === "ordered"} onClick={() => selectSourceQuote(q)}
                              className="px-3 py-1.5 border border-border rounded-sm text-xs hover:border-brand hover:text-brand disabled:opacity-50 transition-colors">
                              Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Send-to-customer form */}
              {rfq.status !== "ordered" && (
                <div className="p-5 border-t border-border">
                  <div className="label-tech text-muted-foreground mb-3">Send quote to customer {cq ? "(re-send / update)" : ""}</div>
                  <form onSubmit={sendToCustomer} className="grid sm:grid-cols-4 gap-4">
                    <label className="block">
                      <span className="label-tech text-muted-foreground block mb-1.5">Source supplier</span>
                      <div className="q-input bg-secondary truncate">{quotes.find((q) => q.id === sendForm.source_quote_id)?.supplier_name || "— pick a row —"}</div>
                    </label>
                    <label className="block">
                      <span className="label-tech text-muted-foreground block mb-1.5">Final unit price ($)</span>
                      <input required type="number" step="0.01" min="0" data-testid="send-unit-price" value={sendForm.unit_price} onChange={(e) => setSendForm((f) => ({ ...f, unit_price: e.target.value }))} className="q-input" />
                    </label>
                    <label className="block">
                      <span className="label-tech text-muted-foreground block mb-1.5">Final lead (days)</span>
                      <input required type="number" min="1" data-testid="send-lead-time" value={sendForm.lead_time_days} onChange={(e) => setSendForm((f) => ({ ...f, lead_time_days: e.target.value }))} className="q-input" />
                    </label>
                    <label className="block">
                      <span className="label-tech text-muted-foreground block mb-1.5">Customer total</span>
                      <div className="q-input bg-secondary font-mono">{sendForm.unit_price ? fmtMoney(Number(sendForm.unit_price) * rfq.quantity) : "—"}</div>
                    </label>
                    <label className="block sm:col-span-4">
                      <span className="label-tech text-muted-foreground block mb-1.5">Note to customer</span>
                      <textarea rows={2} data-testid="send-note" value={sendForm.note} onChange={(e) => setSendForm((f) => ({ ...f, note: e.target.value }))} className="q-input resize-none" placeholder="Optional message shown to the buyer…" />
                    </label>
                    <div className="sm:col-span-4">
                      <button type="submit" disabled={busy || !sendForm.source_quote_id} data-testid="send-quote-button" className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-sm hover:bg-brand-dark disabled:opacity-60 transition-colors">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send quote to customer
                      </button>
                    </div>
                  </form>
                </div>
              )}
              {cq && (
                <div className="p-5 border-t border-border text-sm">
                  <span className="label-tech text-muted-foreground">Current customer quote: </span>
                  <span className="font-mono">{fmtMoney(cq.total_price)} · {cq.lead_time_days}d · from {cq.supplier_name}</span> <StatusBadge status={cq.status === "accepted" ? "accepted" : "sent_to_customer"} />
                </div>
              )}
            </div>
          )}

          {/* Supplier quote form */}
          {isSupplier && (
            <div className="border border-border rounded-sm bg-card">
              <div className="px-5 py-3 border-b border-border label-tech text-muted-foreground">{myQuote ? "Your quote" : "Submit a quote"}</div>
              <div className="p-5">
                {user.supplier_status !== "approved" ? (
                  <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3">
                    Your supplier account is pending admin approval. You can review RFQs but cannot submit quotes yet.
                  </div>
                ) : rfq.status === "ordered" && myQuote?.status !== "accepted" ? (
                  <div className="text-sm text-muted-foreground">This RFQ has been awarded and is closed for new quotes.</div>
                ) : (
                  <form onSubmit={submitQuote} className="grid sm:grid-cols-3 gap-4">
                    <label className="block">
                      <span className="label-tech text-muted-foreground block mb-1.5">Unit price ($)</span>
                      <input required type="number" step="0.01" min="0" data-testid="quote-unit-price"
                        value={quoteForm.unit_price} onChange={(e) => setQuoteForm((f) => ({ ...f, unit_price: e.target.value }))} className="q-input" />
                    </label>
                    <label className="block">
                      <span className="label-tech text-muted-foreground block mb-1.5">Lead time (days)</span>
                      <input required type="number" min="1" data-testid="quote-lead-time"
                        value={quoteForm.lead_time_days} onChange={(e) => setQuoteForm((f) => ({ ...f, lead_time_days: e.target.value }))} className="q-input" />
                    </label>
                    <label className="block">
                      <span className="label-tech text-muted-foreground block mb-1.5">Est. total</span>
                      <div className="q-input bg-secondary font-mono">{quoteForm.unit_price ? fmtMoney(Number(quoteForm.unit_price) * rfq.quantity) : "—"}</div>
                    </label>
                    <label className="block sm:col-span-3">
                      <span className="label-tech text-muted-foreground block mb-1.5">Notes to Nepton</span>
                      <textarea rows={2} data-testid="quote-notes" value={quoteForm.notes} onChange={(e) => setQuoteForm((f) => ({ ...f, notes: e.target.value }))} className="q-input resize-none" placeholder="Capabilities, assumptions, alternatives…" />
                    </label>
                    <div className="sm:col-span-3">
                      <button type="submit" disabled={busy} data-testid="submit-quote-button" className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-sm hover:bg-brand-dark disabled:opacity-60 transition-colors">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {myQuote ? "Update quote" : "Submit quote"}
                      </button>
                      {myQuote && <span className="ml-3 text-sm text-muted-foreground">Current: {fmtMoney(myQuote.total_price)} · {myQuote.lead_time_days}d · <StatusBadge status={myQuote.status} /></span>}
                    </div>
                  </form>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div className="space-y-6">
          {/* Instant estimate (hidden from buyer once a real Nepton quote exists) */}
          {rfq.instant_estimate && !(isBuyer && cq) && (
            <div className="border border-border rounded-sm bg-card" data-testid="instant-estimate">
              <div className="px-5 py-3 border-b border-border flex items-center gap-2">
                <Cpu className="h-4 w-4 text-brand" />
                <span className="label-tech text-muted-foreground">Instant estimate</span>
              </div>
              <div className="p-5">
                <div className="font-heading text-4xl font-bold tracking-tight">{fmtMoney(rfq.instant_estimate.total_price)}</div>
                <div className="text-xs font-mono text-muted-foreground mt-1">rule-based · final quote confirmed by Nepton</div>
                <div className="mt-4 space-y-2 text-sm">
                  <Row l="Unit price" v={fmtMoney(rfq.instant_estimate.unit_price)} />
                  <Row l="Setup fee" v={fmtMoney(rfq.instant_estimate.setup_fee)} />
                  <Row l="Est. lead time" v={`${rfq.instant_estimate.est_lead_time_days} days`} />
                </div>
              </div>
            </div>
          )}

          {/* Messaging */}
          <div className="border border-border rounded-sm bg-card flex flex-col" style={{ height: 460 }}>
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-brand" />
              <span className="label-tech text-muted-foreground">Messages</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="message-thread">
              {msgs.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No messages yet. Start the conversation.</div>}
              {msgs.map((m) => {
                const mine = m.sender_id === user.id;
                return (
                  <div key={m.id} className={cn("flex flex-col", mine ? "items-end" : "items-start")}>
                    <div className={cn("max-w-[85%] px-3 py-2 rounded-sm text-sm", mine ? "bg-primary text-primary-foreground" : "bg-secondary")}>
                      {m.text}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground mt-1">{m.sender_name} · {fmtDateTime(m.created_at)}</div>
                  </div>
                );
              })}
              <div ref={msgEnd} />
            </div>
            <form onSubmit={sendMsg} className="p-3 border-t border-border flex gap-2">
              <input value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="message-input" placeholder="Type a message…"
                className="flex-1 border border-input bg-background px-3 py-2 rounded-sm text-sm outline-none focus:border-brand transition-colors" />
              <button type="submit" data-testid="message-send" className="px-3 bg-brand text-white rounded-sm hover:bg-brand-dark transition-colors">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </div>

      <style>{`.q-input{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));padding:0.55rem 0.75rem;border-radius:0.25rem;font-size:0.9rem;outline:none;transition:border-color .15s}.q-input:focus{border-color:#EA580C}`}</style>
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{l}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}
